import {
  createSlice,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { createStructuredSelector } from "reselect";
import {
  collection,
  documentId,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
} from "firebase/auth";

import { auth } from "../../firebase";

import { database } from "../../firebase";
import { initOrganizations, resetOrganizations } from "./organizationsSlice";
import { serialize, deserialize } from "./utils";
import logger from "../../utils/logger";

const reduxUserFromAuthUser = (authUser) => {
  if (authUser) {
    return {
      id: authUser.uid,
      displayName: authUser.displayName,
      photoURL: authUser.photoURL,
      email: authUser.email,
    };
  }
};

export const dd = createAsyncThunk();

export const initAuth = createAsyncThunk(
  "users/initAuth",
  async (params, { getState, dispatch }) => {
    logger.log("init auth");
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        await dispatch(setAuthState("pending"));
        const uid = user.uid;
        logger.log("user state changed logged in");

        // Update lastLogin timestamp
        try {
          const userDocRef = doc(database, "users", uid);
          await updateDoc(userDocRef, {
            lastLogin: Timestamp.now(),
          });
          logger.log("Updated lastLogin for user", uid);
        } catch (error) {
          logger.error("Error updating lastLogin", error, { userId: uid });
          // Don't fail the login if this fails
        }

        const fUser = await fetchUser(uid, dispatch);
        logger.log("got user", fUser);
        if (fUser) {
          dispatch(setUser(fUser));
        }
        await dispatch(setAuthState("in"));
        await dispatch(initOrganizations());
      } else {
        logger.log("user state changed: logged out");
        if (userUnsubscribe) {
          userUnsubscribe();
          userUnsubscribe = null;
        }
        dispatch(clearUser());
        await dispatch(resetOrganizations());
        await dispatch(setAuthState("out"));
      }
    });
  },
);

let userUnsubscribe = null;

const fetchUser = async (userId, dispatch) => {
  logger.log("fetching user");

  // Unsubscribe from previous listener if it exists
  if (userUnsubscribe) {
    userUnsubscribe();
  }

  const usersRef = collection(database, "users");
  const q = query(usersRef, where(documentId(), "==", userId));

  // Set up real-time listener
  userUnsubscribe = onSnapshot(q, (querySnapshot) => {
    logger.info("querySnapshot", querySnapshot);
    let foundUser;
    querySnapshot.forEach((doc) => {
      if (doc && doc.id === userId) {
        foundUser = { id: doc.id, ...doc.data() };
      }
    });
    if (foundUser) {
      dispatch(setUser(serialize(foundUser)));
    }
  });

  // Return initial user data
  return new Promise((resolve) => {
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      let foundUser;
      querySnapshot.forEach((doc) => {
        if (doc && doc.id === userId) {
          foundUser = { id: doc.id, ...doc.data() };
        }
      });
      if (foundUser) {
        unsubscribe();
        resolve(serialize(foundUser));
      }
    });
  });
};

const usersSlice = createSlice({
  name: "users",
  initialState: {
    authState: null,
    user: null,
    credential: null,
    emailPasswordLoginError: null,
  },
  reducers: {
    setAuthState(state, action) {
      state.authState = action.payload;
    },
    setUser(state, action) {
      state.user = action.payload;
    },
    clearUser(state) {
      state.user = null;
    },
    clearEmailPasswordLoginError(state, action) {
      state.emailPasswordLoginError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginWithEmailAndPassword.fulfilled, (state, action) => {
        if (action.payload) {
          const { error } = action.payload;
          if (Boolean(error)) {
            state.emailPasswordLoginError = error;
          }
        }
      })
      .addCase(signupWithEmailAndPassword.fulfilled, (state, action) => {
        if (action.payload) {
          const { error } = action.payload;
          if (Boolean(error)) {
            state.emailPasswordLoginError = error;
          }
        }
      });
  },
});

export const loginWithGoogle = createAsyncThunk(
  "users/loginWithGoogle",
  async (params, { getState, dispatch }) => {
    await dispatch(setAuthState(null));
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      return {};
    } catch (error) {
      logger.error("error logging in with google", error);
      return { error };
    }
  },
);

export const loginWithEmailAndPassword = createAsyncThunk(
  "users/loginWithUsernamePassword",
  async (params, { getState, dispatch }) => {
    try {
      await dispatch(setAuthState(null));
      const { email, password } = params;
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      let errorMessage;
      const errorCode = error.code;
      switch (errorCode) {
        case "auth/user-not-found":
          errorMessage = "Wrong email or password";
          break;
        case "auth/wrong-password":
          errorMessage = "Wrong email or password";
          break;
        case "auth/invalid-email":
          errorMessage = "The email address is not valid.";
          break;
        case "auth/user-disabled":
          errorMessage = "This user account has been disabled.";
          break;
        case "auth/operation-not-allowed":
          errorMessage = "An internal error occurred. Please try again later.";
          break;
        case "auth/internal-error":
          errorMessage = "An internal error occurred. Please try again later.";
          break;
        default:
          errorMessage = "An internal error occurred. Please try again later.";
      }
      logger.error("error logging in with email and password", error);
      return {
        error: { message: errorMessage },
      };
    }
  },
);

export const signupWithEmailAndPassword = createAsyncThunk(
  "users/signupWithEmailAndPassword",
  async (params, { getState, dispatch }) => {
    try {
      await dispatch(setAuthState(null));
      const { email, password } = params;
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      const errorCode = error.code;
      let errorMessage;
      switch (errorCode) {
        case "auth/email-already-in-use":
          errorMessage =
            "The email address is already in use by another account.";
          break;
        case "auth/invalid-email":
          errorMessage = "The email address is not valid.";
          break;
        case "auth/operation-not-allowed":
          errorMessage = "An internal error occurred. Please try again later.";
          break;
        case "auth/weak-password":
          errorMessage =
            "The password is too weak. Please choose a stronger password.";
          break;
        case "auth/internal-error":
          errorMessage = "An internal error occurred. Please try again later.";
          break;
        default:
          errorMessage = "An internal error occurred. Please try again later.";
      }
      logger.error("error signing up with email and password", error);
      return {
        error: { message: errorMessage },
      };
    }
  },
);

export const logout = createAsyncThunk(
  "users/logout",
  async (params, { getState, dispatch }) => {
    try {
      await signOut(auth);
    } catch (error) {
      logger.error("error signing out", error);
    }
  },
);

const authStateSelector = (state) => state.users.authState;
const userRawSelector = (state) => state.users.user;
const emailPasswordErrorSelector = (state) =>
  state.users.emailPasswordLoginError;

// Deserializing selector for user
const userSelector = createSelector([userRawSelector], (storedUser) => {
  if (!storedUser) {
    return null;
  }
  return deserialize(storedUser);
});

const firebaseUserSelector = createSelector(
  [userSelector, authStateSelector],
  (storedUser, authState) => {
    // If we have a stored user from Firestore, use it (includes superAdmin, etc.)
    if (storedUser) {
      return storedUser;
    }
    // Otherwise fall back to Firebase Auth user data
    return reduxUserFromAuthUser(auth.currentUser);
  },
);

export const usersSliceSelector = createStructuredSelector({
  user: firebaseUserSelector,
  authState: authStateSelector,
  emailPasswordError: emailPasswordErrorSelector,
});

export default usersSlice.reducer;
export const {
  setAuthInitialized,
  setAuthState,
  setUser,
  clearUser,
  clearEmailPasswordLoginError,
} = usersSlice.actions;
